import { useSelector } from "react-redux"
import { useEffect, useMemo, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { useSession } from "../../auth/session"
import { RootState } from "../../redux/store"
import { AppDispatch } from "../../redux/store"
import { useActivePlans, useExtraTrialDays } from "../../redux/plansSlice"
import { useSku } from "../../redux/skusSlice"
import Api from "../../api/api"
import Spinner from "../widgets/Spinner"
import MoreMenu, { MoreMenuItem } from "../widgets/MoreMenu"
import { useSubscription, switchSubscriptionThunk, cancelSubscriptionThunk, resumeSubscriptionThunk } from "../../redux/subscriptionSlice"
import { FetchableStateStatus } from "../../redux/fetchableState"
import { Offering, Sku, SubscriptionStatus } from "../../api/models"
import { backdropDismiss } from "../../utils/backdropDismiss"

// A tier is a sku with subscription offerings; its billing periods are the
// offerings themselves (monthly/yearly toggle on one card).

export function billingPeriodLabel(intervalUnit: string | null, intervalCount: number | null): string {
  intervalUnit = intervalUnit ?? "month"
  intervalCount = intervalCount ?? 1
  if (intervalCount === 1) {
    switch (intervalUnit) {
      case "day": return "Daily"
      case "week": return "Weekly"
      case "month": return "Monthly"
      case "year": return "Yearly"
    }
  }
  const unitLabel = intervalUnit === "day" ? "days" :
    intervalUnit === "week" ? "weeks" :
      intervalUnit === "month" ? "months" : "years"
  return `Every ${intervalCount} ${unitLabel}`
}

function billingPeriodShort(intervalUnit: string | null, intervalCount: number | null): string {
  intervalUnit = intervalUnit ?? "month"
  intervalCount = intervalCount ?? 1
  if (intervalCount === 1) {
    switch (intervalUnit) {
      case "day": return "/day"
      case "week": return "/wk"
      case "month": return "/mo"
      case "year": return "/yr"
    }
  }
  return `/${intervalCount}${intervalUnit.charAt(0)}`
}

function subscriptionOfferings(sku: Sku | undefined): Offering[] {
  return (sku?.offerings ?? [])
    .filter(o => o.paymentType === "subscription")
    .slice()
    .sort((a, b) => a.priceCents - b.priceCents)
}

export default function SubscriptionPage() {
  const session = useSession()
  const accessToken = session.accessToken || ""
  const { data: subscription, status: subscriptionStatus } = useSubscription(session.accessToken || "")
  const { data: activeSkuIds, status: activePlansStatus } = useActivePlans(accessToken)

  const skuEntities = useSelector((state: RootState) => state.skus.entities)
  const subscriptionBusy = subscriptionStatus !== FetchableStateStatus.Fetched
  const currentSkuId: string | undefined = subscription?.skuId
  const currentOfferingId: string | undefined = subscription?.offeringId ?? undefined

  // activeSkuIds arrive sorted by cheapest subscription offering (plansSlice)
  const currentTierIndex = useMemo(
    () => activeSkuIds?.findIndex(id => id === currentSkuId) ?? -1,
    [activeSkuIds, currentSkuId]
  )
  const hasMultiPeriodCards = useMemo(
    () => (activeSkuIds ?? []).some(id => subscriptionOfferings(skuEntities[id]?.data).length > 1),
    [activeSkuIds, skuEntities]
  )
  const currentSkuData = currentSkuId ? skuEntities[currentSkuId]?.data : undefined
  const currentOffering = currentSkuData?.offerings.find(o => o.id === currentOfferingId)
  const currentBillingPeriod = currentOffering ? { intervalUnit: currentOffering.intervalUnit, intervalCount: currentOffering.intervalCount } : undefined

  const [resetToCurrentPlan, setResetToCurrentPlan] = useState(0)

  return (
    <div className="page" style={{ maxWidth: 1000 }}>
      <h1 className="page-title">Rent-to-Own</h1>

      < CurrentPlanBanner onClick={() => setResetToCurrentPlan(n => n + 1)} />
      <ExtraTrialDaysBanner />
      {
        activePlansStatus === FetchableStateStatus.Initializing || activeSkuIds === undefined ?
          (
            <p className="empty-state"><Spinner size={16} /> Loading plans…</p>
          ) :
          <div className="tier-grid">
            {
              activeSkuIds.map((skuId, tierIndex) => {
                const relationToCurrent = currentTierIndex === -1 ? undefined :
                  tierIndex === currentTierIndex ? "current" :
                    tierIndex < currentTierIndex ? "downgrade" :
                      "upgrade"
                return (
                  <TierCard
                    key={skuId}
                    skuId={skuId}
                    currentOfferingId={currentOfferingId}
                    currentBillingPeriod={currentBillingPeriod}
                    relationToCurrent={relationToCurrent}
                    subscriptionCancelled={subscription?.status === "cancelled" || subscription?.status === "paused"}
                    subscriptionBusy={subscriptionBusy}
                    hasMultiPeriodCards={hasMultiPeriodCards}
                    resetToCurrentPlan={resetToCurrentPlan}
                    currentTone={tierToneFor(subscription?.status)}
                  />
                )
              })
            }
          </div>}

    </div>
  )
}

// Attention banner surfacing the extra free-trial days the user has unlocked by
// owning eligible products. Shared across all plans, so it lives outside the tier
// cards. The store only carries entries when the user has no subscription yet.
function ExtraTrialDaysBanner() {
  const extraTrialDays = useExtraTrialDays()
  const [popupOpen, setPopupOpen] = useState(false)
  const popupRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!popupOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) setPopupOpen(false)
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [popupOpen])

  const totalDays = extraTrialDays.reduce((sum, e) => sum + e.days, 0)
  if (extraTrialDays.length === 0 || totalDays <= 0) return null

  return (
    <div className="extra-trial-banner">
      <span className="extra-trial-icon" aria-hidden="true">&#127873;</span>
      <span className="extra-trial-text">
        You've unlocked <strong>{totalDays} extra {totalDays === 1 ? "day" : "days"}</strong> of free trial on any plan for the products you already own.
      </span>
      <div ref={popupRef} className="extra-trial-info">
        <button
          className="extra-trial-info-btn"
          aria-label="See which products granted extra trial days"
          onClick={() => setPopupOpen(o => !o)}
        >
          &#9432;
        </button>
        {popupOpen && (
          <div className="extra-trial-popup">
            <div className="extra-trial-popup-title">Where your extra days come from</div>
            <ul className="extra-trial-popup-list">
              {extraTrialDays.map(e => (
                <li key={e.productId}>
                  <ExtraTrialProductName productId={e.productId} />
                  <span className="extra-trial-popup-days">+{e.days} {e.days === 1 ? "day" : "days"}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}

function ExtraTrialProductName({ productId }: { productId: string }) {
  const product = useSelector((state: RootState) => state.products.entities[productId]?.data)
  return <span className="extra-trial-popup-product">{product?.name ?? "Your product"}</span>
}

type TierTone = "blue" | "yellow" | "red"

function tierToneFor(status?: SubscriptionStatus): TierTone {
  switch (status) {
    case "paused":
    case "past_due":
      return "yellow"
    case "unpaid":
    case "cancelled":
      return "red"
    default:
      return "blue"
  }
}

type BannerActionType = "cancel" | "resume" | "update_payment"

interface StatusBannerConfig {
  pillLabel: string
  pillClass: string
  dateLabel: string
  dateField: "endsAt" | "renewsAt"
  dateColor?: "red" | "grey"
  topAction: BannerActionType | null
  menuActions: BannerActionType[]
}

const STATUS_BANNER_CONFIG: Record<SubscriptionStatus, StatusBannerConfig> = {
  on_trial: { pillLabel: "Trial", pillClass: "status-on-trial", dateLabel: "Trial ends", dateField: "renewsAt", topAction: "cancel", menuActions: ["update_payment"] },
  active: { pillLabel: "Active", pillClass: "status-active", dateLabel: "Renews", dateField: "renewsAt", topAction: "cancel", menuActions: ["update_payment"] },
  paused: { pillLabel: "Paused", pillClass: "status-paused", dateLabel: "Resumes", dateField: "renewsAt", topAction: "resume", menuActions: ["cancel", "update_payment"] },
  past_due: { pillLabel: "Past due", pillClass: "status-past-due", dateLabel: "Renews", dateField: "renewsAt", topAction: "update_payment", menuActions: ["cancel"] },
  unpaid: { pillLabel: "Unpaid", pillClass: "status-unpaid", dateLabel: "Ends", dateField: "endsAt", dateColor: "red", topAction: "update_payment", menuActions: ["cancel"] },
  cancelled: { pillLabel: "Cancelled", pillClass: "status-cancelled", dateLabel: "Ends", dateField: "endsAt", dateColor: "red", topAction: "resume", menuActions: [] },
  expired: { pillLabel: "Expired", pillClass: "status-expired", dateLabel: "Ended", dateField: "endsAt", dateColor: "grey", topAction: null, menuActions: [] },
}

function CurrentPlanBanner({ onClick }: { onClick?: () => void }) {
  const session = useSession()
  const accessToken = session.accessToken || ""
  const { data: subscription, status: subscriptionStatus } = useSubscription(accessToken)
  const dispatch = useDispatch<AppDispatch>()
  const [resuming, setResuming] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const cancelDialogRef = useRef<HTMLDialogElement>(null)

  if (subscriptionStatus === FetchableStateStatus.Initializing)
    return (
      <div className="current-plan-banner"><Spinner size={12} />Loading...</div>
    )

  if (!subscription)
    return (
      <div className="current-plan-banner">No active rent-to-own plan</div>
    )

  const config = STATUS_BANNER_CONFIG[subscription.status]
  if (!config)
    return (
      <div className="current-plan-banner">Unknown rent-to-own plan status</div>
    )

  const dateValue = subscription[config.dateField]
  const dateColorStyle =
    config.dateColor === "red" ? { color: "#e07470" } :
      config.dateColor === "grey" ? { color: "var(--nfa-light-grey)" } :
        undefined

  const cancelAction = (
    <button
      key="cancel-top"
      className="card-link-danger"
      disabled={cancelling}
      onClick={(e) => { e.stopPropagation(); cancelDialogRef.current?.showModal() }}
    >
      {cancelling ? <><Spinner size={12} /> cancelling...</> : "Cancel plan"}
    </button>
  )

  const resumeLabel = subscription.status === "paused" ? "Resume now" : "Resume plan"
  const resumeAction = (
    <button
      key="resume-top"
      className="card-link"
      style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
      disabled={resuming}
      onClick={async (e) => {
        e.stopPropagation()
        setResuming(true)
        try { await dispatch(resumeSubscriptionThunk({ accessToken })).unwrap() } catch (err) { console.error("Failed to resume:", err) }
        setResuming(false)
      }}
    >
      {resuming ? <><Spinner size={12} /> resuming...</> : resumeLabel}
    </button>
  )

  const updatePaymentTopAction = subscription.updatePaymentMethodUrl ? (
    <a
      key="update-top"
      href={subscription.updatePaymentMethodUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="card-link"
      onClick={(e) => e.stopPropagation()}
    >
      Update payment method
    </a>
  ) : null

  const renderTopAction = (action: BannerActionType) =>
    action === "cancel" ? cancelAction :
      action === "resume" ? resumeAction :
        action === "update_payment" ? updatePaymentTopAction :
          null

  const menuItem = (action: BannerActionType): MoreMenuItem | null => {
    if (action === "cancel")
      return { label: "Cancel plan", danger: true, onClick: () => cancelDialogRef.current?.showModal() }
    if (action === "update_payment" && subscription.updatePaymentMethodUrl)
      return { label: "Update payment method", href: subscription.updatePaymentMethodUrl, external: true }
    return null
  }

  const topActionElement = config.topAction ? renderTopAction(config.topAction) : null
  const menuItems = config.menuActions.map(menuItem).filter((it): it is MoreMenuItem => it !== null)
  const showMenu = menuItems.length > 0
  const hasRightContent = !!dateValue || !!topActionElement || showMenu

  return (
    <div className="current-plan-banner" onClick={onClick} style={{ cursor: "pointer" }}>
      <CurrentPlanBannerActiveSubscription skuId={subscription.skuId} offeringId={subscription.offeringId} />
      <span className={`status-badge ${config.pillClass}`}>{config.pillLabel}</span>
      {hasRightContent && (
        <span className="banner-right">
          {dateValue && (
            <span className="detail-label" style={dateColorStyle}>
              {config.dateLabel} {new Date(dateValue).toLocaleDateString()}
            </span>
          )}
          {topActionElement}
          {showMenu && <MoreMenu items={menuItems} label="Subscription actions" />}
        </span>
      )}
      <dialog ref={cancelDialogRef} className="redeem-dialog" {...backdropDismiss(() => { cancelDialogRef.current?.close() })}>
        <div className="redeem-dialog-content">
          <button className="redeem-dialog-close" onClick={() => cancelDialogRef.current?.close()} aria-label="Close">&times;</button>
          <h2 className="redeem-dialog-title">Cancel Plan</h2>
          <p>Are you sure you want to cancel your plan? You will retain access until {subscription.renewsAt ? new Date(subscription.renewsAt).toLocaleDateString() : "the end of the current billing period"}.</p>
          <div className="redeem-actions">
            <button onClick={() => cancelDialogRef.current?.close()}>Keep Subscription</button>
            <button className="btn-danger" disabled={cancelling} onClick={async () => {
              setCancelling(true)
              cancelDialogRef.current?.close()
              try { await dispatch(cancelSubscriptionThunk({ accessToken })).unwrap() } catch (err) { console.error("Failed to cancel:", err) }
              setCancelling(false)
            }}>
              {cancelling ? <><Spinner size={12} /> cancelling...</> : "Cancel Plan"}
            </button>
          </div>
        </div>
      </dialog>
    </div>
  )
}

function CurrentPlanBannerActiveSubscription({ skuId, offeringId }: { skuId: string, offeringId: string | null }) {
  const { data: sku } = useSku(skuId)

  if (!sku) return null

  const offering = offeringId !== null ? sku.offerings.find(o => o.id === offeringId) : undefined

  return (
    <span className="current-plan-info">
      Your current plan: <strong>{sku.name}</strong>
      {offering && <>
        {" "}
        <span className="detail-label">({billingPeriodLabel(offering.intervalUnit, offering.intervalCount).toLowerCase()})</span>
      </>}
    </span>
  )
}

// One card per tier sku; its subscription offerings are the billing periods,
// toggled inside the card.
function TierCard({
  skuId,
  currentOfferingId,
  currentBillingPeriod,
  relationToCurrent,
  subscriptionCancelled,
  subscriptionBusy,
  hasMultiPeriodCards,
  resetToCurrentPlan,
  currentTone,
}: {
  skuId: string;
  currentOfferingId?: string;
  currentBillingPeriod?: { intervalUnit: string | null; intervalCount: number | null };
  relationToCurrent?: "downgrade" | "current" | "upgrade";
  subscriptionCancelled?: boolean;
  subscriptionBusy?: boolean;
  hasMultiPeriodCards?: boolean;
  resetToCurrentPlan?: number;
  currentTone?: TierTone;
}) {
  const session = useSession()
  const accessToken = session.accessToken || ""
  const dispatch = useDispatch<AppDispatch>()
  const { data: sku, status: skuStatus } = useSku(skuId)
  const totalExtraTrialDays = useExtraTrialDays().reduce((sum, e) => sum + e.days, 0)

  const offerings = useMemo(() => subscriptionOfferings(sku), [sku])

  const isCurrentTier = relationToCurrent === "current"
  const currentOfferingInTier = isCurrentTier ? offerings.find(o => o.id === currentOfferingId) : undefined

  // Default to the current offering when this is the current tier, otherwise
  // match the user's current billing period, otherwise the cheapest offering
  const [selectedOfferingId, setSelectedOfferingId] = useState<string | undefined>(undefined)
  useEffect(() => {
    if (!selectedOfferingId || !offerings.some(o => o.id === selectedOfferingId)) {
      if (currentOfferingInTier) {
        setSelectedOfferingId(currentOfferingInTier.id)
        return
      }
      if (currentBillingPeriod) {
        const match = offerings.find(o => o.intervalUnit === currentBillingPeriod.intervalUnit && o.intervalCount === currentBillingPeriod.intervalCount)
        if (match) {
          setSelectedOfferingId(match.id)
          return
        }
      }
      setSelectedOfferingId(offerings[0]?.id)
    }
  }, [offerings, currentOfferingInTier, currentBillingPeriod, selectedOfferingId])

  // Reset to the current offering when the banner is clicked
  useEffect(() => {
    if (resetToCurrentPlan && currentOfferingInTier) {
      setSelectedOfferingId(currentOfferingInTier.id)
    }
  }, [resetToCurrentPlan, currentOfferingInTier])

  const selectedOffering = offerings.find(o => o.id === selectedOfferingId) ?? offerings[0]
  const [changingPlan, setChangingPlan] = useState(false)

  const isBusy = skuStatus === FetchableStateStatus.Initializing || skuStatus === FetchableStateStatus.Loading

  if (!sku || !selectedOffering) {
    return (
      <div className="tier-card">
        <h2 className="tier-name"><Spinner size={12} /></h2>
      </div>
    )
  }

  // legacy subscriptions without an offeringId count the whole tier as current
  const isCurrent = isCurrentTier && (currentOfferingId === undefined || selectedOffering.id === currentOfferingId)

  // Within the current tier, another billing period shows "Switch"
  let effectiveRelation = relationToCurrent
  if (isCurrentTier && !isCurrent) {
    effectiveRelation = "upgrade"
  }

  const hasActiveSubscription = relationToCurrent !== undefined

  const handleSelect = async () => {
    if (isBusy) return
    if (isCurrent && subscriptionCancelled) {
      setChangingPlan(true)
      try {
        await dispatch(resumeSubscriptionThunk({ accessToken })).unwrap()
      } catch (err) {
        console.error("Failed to resume subscription:", err)
      }
      setChangingPlan(false)
      return
    }
    if (isCurrent) return
    setChangingPlan(true)
    try {
      if (hasActiveSubscription) {
        await dispatch(switchSubscriptionThunk({ accessToken, offeringId: selectedOffering.id })).unwrap()
        setChangingPlan(false)
      } else {
        const { checkout } = await Api.makeSkuCheckout({
          skuId: sku.id,
          offeringId: selectedOffering.id,
          redirectUrl: window.location.origin,
          extraTrialDays: totalExtraTrialDays > 0 ? totalExtraTrialDays : undefined,
          accessToken
        })
        window.location.href = checkout.attributes.url
      }
    } catch (err) {
      console.error(hasActiveSubscription ? "Plan switch failed:" : "Checkout failed:", err)
      setChangingPlan(false)
    }
  }

  let buttonTitle: string
  if (isCurrent && subscriptionCancelled) {
    buttonTitle = changingPlan ? "resuming..." : "Resume Subscription"
  } else if (isCurrent) {
    buttonTitle = "Current Plan"
  } else if (effectiveRelation === undefined) {
    buttonTitle = "Select"
  } else if (isCurrentTier) {
    buttonTitle = changingPlan ? "switching..." : "Switch"
  } else {
    switch (effectiveRelation) {
      case "downgrade": buttonTitle = changingPlan ? "downgrading..." : "Downgrade"; break
      case "upgrade": buttonTitle = changingPlan ? "upgrading..." : "Upgrade"; break
      default: buttonTitle = "Select"
    }
  }

  // Calculate savings for yearly vs monthly
  const savingsBadges = computeSavingsBadges(offerings)

  const toneClass = isCurrent && currentTone ? ` tier-tone-${currentTone}` : ""

  return (
    <div className={`tier-card${isCurrent ? " tier-card-current" : ""}${isCurrent && subscriptionCancelled ? " tier-card-cancelled" : ""}${toneClass}`}>
      {isCurrent && <div className="tier-current-tag">Current</div>}
      <h2 className="tier-name">{sku.name}</h2>

      {offerings.length > 1 ? (
        <div className="billing-toggle">
          {offerings.map(offering => (
            <button
              key={offering.id}
              className={`billing-toggle-option${offering.id === selectedOffering.id ? " billing-toggle-option-active" : ""}`}
              onClick={() => setSelectedOfferingId(offering.id)}
            >
              {billingPeriodLabel(offering.intervalUnit, offering.intervalCount)}
              {savingsBadges[offering.id] && (
                <span className="billing-save-badge">Save {savingsBadges[offering.id]}%</span>
              )}
            </button>
          ))}
        </div>
      ) : hasMultiPeriodCards && (
        <div className="billing-label">Billed {billingPeriodLabel(selectedOffering.intervalUnit, selectedOffering.intervalCount).toLowerCase()}</div>
      )}

      <div className="tier-price">
        <span className="tier-price-amount">${(selectedOffering.priceCents / 100).toFixed(2)}</span>
        <span className="tier-price-period">{billingPeriodShort(selectedOffering.intervalUnit, selectedOffering.intervalCount)}</span>
      </div>
      <PlanTrial offering={selectedOffering} />
      <PlanPoints offering={selectedOffering} />
      <PlanHighlights plan={sku} />
      <button
        className={`tier-action ${isCurrent && subscriptionCancelled ? "btn-upgrade" : isCurrent ? "" : "btn-upgrade"}`}
        disabled={(isCurrent && !subscriptionCancelled) || (!isCurrent && subscriptionCancelled) || isBusy || subscriptionBusy}
        onClick={handleSelect}
      >
        {(isBusy || changingPlan) && <Spinner size={12} />} {buttonTitle}
      </button>
    </div>
  )
}

function computeSavingsBadges(offerings: Offering[]): Record<string, number> {
  if (offerings.length < 2) return {}

  // Find the monthly-equivalent cost of the cheapest (shortest) offering as baseline
  const withMonthlyCost = offerings.map(o => ({
    offering: o,
    monthlyEquivalent: monthlyEquivalentCost(o)
  }))

  const baseline = Math.max(...withMonthlyCost.map(o => o.monthlyEquivalent))
  const badges: Record<string, number> = {}

  for (const { offering, monthlyEquivalent } of withMonthlyCost) {
    if (monthlyEquivalent < baseline) {
      const saving = Math.round((1 - monthlyEquivalent / baseline) * 100)
      if (saving > 0) badges[offering.id] = saving
    }
  }

  return badges
}

function monthlyEquivalentCost(offering: Offering): number {
  const intervalCount = offering.intervalCount ?? 1
  let months: number
  switch (offering.intervalUnit) {
    case "day": months = intervalCount / 30; break
    case "week": months = intervalCount / 4.33; break
    case "month": months = intervalCount; break
    case "year": months = intervalCount * 12; break
    default: months = 1
  }
  return offering.priceCents / months
}

// Total free-trial length for an offering = its standard trial plus any extra
// days the user unlocked from products they own (shared across all plans). The
// breakdown line only appears when unlocked days actually apply.
function PlanTrial({ offering }: { offering: Offering }) {
  const accessToken = useSession().accessToken || ""
  const { data: subscription } = useSubscription(accessToken)
  const extraTrialDays = useExtraTrialDays()
  const extraDays = extraTrialDays.reduce((sum, e) => sum + e.days, 0)
  const standardDays = offering.trialDays ?? 0
  const totalDays = standardDays + extraDays

  // Trials only apply to new subscribers — switching/upgrading plans on an
  // existing (non-expired) subscription grants no trial, so hide it there.
  if (subscription && subscription.status !== "expired") return null

  if (totalDays <= 0) return null

  const breakdown = extraDays > 0
    ? (standardDays > 0 ? `${standardDays} standard + ${extraDays} unlocked` : `${extraDays} unlocked`)
    : null

  return (
    <div className="tier-trial">
      <span className="tier-trial-total">{totalDays}-day free trial</span>
      {breakdown && <span className="tier-trial-breakdown">{breakdown}</span>}
    </div>
  )
}

function PlanHighlights({ plan }: { plan: Sku }) {
  return <>
    {
      plan.highlights.length > 0 && (
        <ul className="tier-features">
          {plan.highlights.map((f: string, i: number) => (
            <li key={i}>{f}</li>
          ))}
        </ul>
      )
    }
  </>
}

function PlanPoints({ offering }: { offering: Offering }) {
  if (offering.rewardPoints <= 0)
    return null

  return (
    <div className="tier-points">
      <span className="tier-features">Earn </span>
            <span className="tier-points-amount">{offering.rewardPoints}</span>
      <span className="tier-features"> reward points on each renewal!</span>
    </div>
  )
}
